import { useEffect, useState } from "react";
import { REMAINING_PCT } from "@/lib/inventory";

/**
 * TicketsUrgencyBar — static inventory notice that ONLY appears
 * while the OnTourHQ checkout modal is open (i.e. after someone clicks Get Tickets).
 *
 * 2026-07-06: no price rise is planned — copy is inventory-based ("less than N%
 * of tickets remain"), N lives in @/lib/inventory. The old 15-min price-hold
 * countdown was removed along with its premise.
 * Each visitor gets a personal short timer so the deadline FEELS like minutes, not weeks,
 * shown at the exact moment they're deciding (the checkout). The July 1 bump is what makes
 * the "pricing increasing" message true.
 *
 * WHY top-0 + MAX Z-INDEX
 * OnTourHQ's checkout renders body-level overlay + iframe at z-index ~2,147,483,646 with a
 * full gray-out. A normal banner sits UNDER it and vanishes during checkout — exactly when
 * urgency matters. This bar sits at z 2,147,483,647 (one above) so it floats over the modal;
 * the modal's ✕ and title render below it.
 */

// ── Tunables ────────────────────────────────────────────────
const DURATION_MIN = 15;                  // personal timer length
const STORAGE_KEY = "yrd_price_timer_v1"; // bump suffix to reset everyone
// ────────────────────────────────────────────────────────────

function getExpiry(): number {
  if (typeof window === "undefined") return Date.now() + DURATION_MIN * 60_000;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const n = parseInt(saved, 10);
      if (!Number.isNaN(n)) return n;
    }
  } catch { /* localStorage blocked — fall through */ }
  const exp = Date.now() + DURATION_MIN * 60_000;
  try { window.localStorage.setItem(STORAGE_KEY, String(exp)); } catch { /* ignore */ }
  return exp;
}

// True when OnTourHQ's modal overlay/iframe is present AND visible.
function isCheckoutOpen(): boolean {
  const nodes = document.querySelectorAll("body > div, body > iframe");
  for (const el of Array.from(nodes)) {
    const s = window.getComputedStyle(el);
    const z = parseInt(s.zIndex, 10);
    if (
      z > 100000 &&
      s.display !== "none" &&
      s.visibility !== "hidden" &&
      el.getBoundingClientRect().width > 50
    ) {
      return true;
    }
  }
  return false;
}

const pad = (n: number) => String(n).padStart(2, "0");

const TicketsUrgencyBar = () => {
  const [expiry] = useState<number>(() => getExpiry());
  const [now, setNow] = useState<number>(() => Date.now());
  const [open, setOpen] = useState<boolean>(false);

  // Tick the clock once per second.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Watch the DOM for the OnTourHQ modal opening/closing.
  useEffect(() => {
    const update = () => setOpen(isCheckoutOpen());
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    return () => obs.disconnect();
  }, []);

  if (!open) return null;

  const diff = expiry - now;
  const expired = diff <= 0;
  const minutes = Math.max(0, Math.floor(diff / 60_000));
  const seconds = Math.max(0, Math.floor((diff / 1000) % 60));

  return (
    <div
      id="yrd-urgency-bar"
      role="status"
      aria-live="polite"
      style={{ zIndex: 2147483647 }}
      className="fixed top-0 inset-x-0 w-full bg-yacht-yellow text-yacht-charcoal
        py-2.5 md:py-3 px-4 flex items-center justify-center gap-2 md:gap-3
        border-b border-black/10 shadow-[0_6px_24px_-8px_rgba(0,0,0,0.45)] select-none"
    >
      <span className="text-base md:text-lg" aria-hidden>⏳</span>

      <span className="text-[13px] md:text-base font-bold tracking-tight">
        Final wave — less than {REMAINING_PCT}% of tickets remain. Secure your spot now.
      </span>
    </div>
  );
};

export default TicketsUrgencyBar;
